import * as pc from 'playcanvas';

/**
 * Loader for `.splat` files, registered on PlayCanvas's gsplat handler next to SPZ.
 *
 * PlayCanvas 2.21 replaced the gsplat handler's "unknown extension -> PLY parser"
 * fallback with a parser registry that has no `.splat` entry, so every `.splat` URL
 * failed with "No parser found for resource". Two kinds of file use the extension:
 *   - The antimatter15 layout (the common one, written by the Babylon-era uploader,
 *     SuperSplat and most web viewers): headerless 32-byte records.
 *   - A PLY that was only named `.splat`. These loaded through the old fallback and
 *     are handed back to PlayCanvas's own PLY parser.
 *
 * The decoded data is in the same frame as PLY, so it keeps the viewer's legacy
 * Y/Z flip (see utils/splatFrame).
 */

const SPLAT_ROW_BYTES = 32;
const SH_C0 = 0.28209479177387814;
const OPACITY_EPSILON = 1e-6;

type PlyElements = ConstructorParameters<typeof pc.GSplatData>[0];

type ParserUrl = { load: string; original: string };
type ParserCallback = (err: unknown, resource?: unknown) => void;
type ParserContext = { url: string | null; ext: string; basename: string };
type GsplatParser = {
    canParse(context: ParserContext): boolean;
    load(url: ParserUrl, callback: ParserCallback, asset?: pc.Asset): void;
};
type GsplatHandler = {
    parsers: GsplatParser[];
    fetch(url: ParserUrl, responseType: string, callback: (err: unknown, data?: ArrayBuffer) => void, asset?: pc.Asset): void;
};

/** True when the buffer starts with the PLY magic (`ply` + newline). */
export function isPlyBuffer(buffer: ArrayBuffer): boolean {
    if (buffer.byteLength < 4) return false;
    const head = new Uint8Array(buffer, 0, 4);
    return head[0] === 0x70 && head[1] === 0x6c && head[2] === 0x79 && (head[3] === 0x0a || head[3] === 0x0d);
}

/**
 * Decode an antimatter15 `.splat` buffer into PLY-style gaussian columns: linear
 * scales become log scales, RGBA bytes become SH DC terms plus logit opacity, and
 * the byte-packed quaternion (rot_0 = w) is unpacked and normalized.
 */
export function decodeSplatBuffer(buffer: ArrayBuffer): PlyElements {
    const count = Math.floor(buffer.byteLength / SPLAT_ROW_BYTES);
    if (count === 0) {
        throw new Error('Invalid .splat file: no splat records');
    }
    if (buffer.byteLength % SPLAT_ROW_BYTES !== 0) {
        console.warn(`[SPLAT] .splat size ${buffer.byteLength} is not a multiple of ${SPLAT_ROW_BYTES} bytes; ignoring the trailing partial record`);
    }

    const names = [
        'x', 'y', 'z',
        'scale_0', 'scale_1', 'scale_2',
        'f_dc_0', 'f_dc_1', 'f_dc_2', 'opacity',
        'rot_0', 'rot_1', 'rot_2', 'rot_3'
    ];
    const columns = names.map(() => new Float32Array(count));
    const [x, y, z, s0, s1, s2, r, g, b, opacity, q0, q1, q2, q3] = columns;

    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < count; i++) {
        const o = i * SPLAT_ROW_BYTES;
        x[i] = view.getFloat32(o, true);
        y[i] = view.getFloat32(o + 4, true);
        z[i] = view.getFloat32(o + 8, true);
        s0[i] = Math.log(view.getFloat32(o + 12, true));
        s1[i] = Math.log(view.getFloat32(o + 16, true));
        s2[i] = Math.log(view.getFloat32(o + 20, true));
        r[i] = (bytes[o + 24] / 255 - 0.5) / SH_C0;
        g[i] = (bytes[o + 25] / 255 - 0.5) / SH_C0;
        b[i] = (bytes[o + 26] / 255 - 0.5) / SH_C0;
        const alpha = Math.min(1 - OPACITY_EPSILON, Math.max(OPACITY_EPSILON, bytes[o + 27] / 255));
        opacity[i] = Math.log(alpha / (1 - alpha));
        // The writer packs each normalized component as `v * 128 + 128`.
        const w = (bytes[o + 28] - 128) / 128;
        const qx = (bytes[o + 29] - 128) / 128;
        const qy = (bytes[o + 30] - 128) / 128;
        const qz = (bytes[o + 31] - 128) / 128;
        const len = Math.hypot(w, qx, qy, qz);
        if (len > 0) {
            q0[i] = w / len;
            q1[i] = qx / len;
            q2[i] = qy / len;
            q3[i] = qz / len;
        } else {
            q0[i] = 1;
        }
    }

    return [{
        name: 'vertex',
        count,
        properties: names.map((name, i) => ({ type: 'float', name, storage: columns[i], byteSize: 4 }))
    }];
}

export class SplatFormatParser {
    app: pc.AppBase;

    /** Set by the gsplat handler's addParser(). */
    handler: GsplatHandler | null = null;

    constructor(app: pc.AppBase) {
        this.app = app;
    }

    canParse(context: ParserContext): boolean {
        return context.ext === 'splat';
    }

    load(url: ParserUrl, callback: ParserCallback, asset?: pc.Asset): void {
        const handler = this.handler;
        if (!handler) {
            callback('SplatFormatParser: not registered on a gsplat handler');
            return;
        }
        handler.fetch(url, pc.Http.ResponseType.ARRAY_BUFFER, (err, data) => {
            if (err) {
                callback(err);
                return;
            }
            if (!data) {
                callback(`Empty response for ${url.original}`);
                return;
            }
            if (isPlyBuffer(data)) {
                this.loadAsPly(url, data, callback, asset);
                return;
            }
            try {
                const gsplatData = new pc.GSplatData(decodeSplatBuffer(data));
                // Same spatial reorder PlayCanvas's PLY parser applies by default.
                if ((asset?.data as { reorder?: boolean } | undefined)?.reorder ?? true) {
                    gsplatData.reorderData();
                }
                const prepareCenters = (this.app.scene as { gsplatCentersEnabled?: boolean } | undefined)?.gsplatCentersEnabled !== false;
                callback(null, new pc.GSplatResource(this.app.graphicsDevice, gsplatData, { prepareCenters }));
            } catch (e) {
                callback(e);
            }
        }, asset);
    }

    open(_url: string, data: unknown): unknown {
        return data;
    }

    /** Hand an already-downloaded PLY body to PlayCanvas's PLY parser without refetching. */
    private loadAsPly(url: ParserUrl, data: ArrayBuffer, callback: ParserCallback, asset?: pc.Asset): void {
        const plyParser = this.handler?.parsers.find((parser) =>
            parser !== (this as unknown) && parser.canParse({ url: 'model.ply', ext: 'ply', basename: 'model.ply' })
        );
        if (!plyParser) {
            callback(`PLY parser unavailable for ${url.original}`);
            return;
        }
        const blobUrl = URL.createObjectURL(new Blob([data]));
        plyParser.load({ load: blobUrl, original: url.original }, (err, resource) => {
            URL.revokeObjectURL(blobUrl);
            callback(err, resource);
        }, asset);
    }
}
